import PropTypes from "prop-types";
import React, { useContext } from "react";

import { ElementVisibilityContext } from "@swa-ui/browser";
import { BulletList, Button, Card, Text } from "@swa-ui/core";
import { Currency } from "@swa-ui/currency";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import styles from "./FareOptionDetails.module.scss";

/**
 * This is a psuedo implementation of FareOptionDetails for layout purposes only.
 * actual implementation will be completed as part of:
 * https://southwest.atlassian.net/browse/ATLASMON-853
 *
 */

export const FareOptionDetails = (props) => {
  const { fareOptionDetails, metadata } = props;
  const { amount, accrualPoints, currencyCode, features, name } = fareOptionDetails;
  const { registerElement } = useContext(ElementVisibilityContext);

  return (
    <div className={styles.content} ref={(element) => registerElement(element, metadata)}>
      <Card {...getCardProps()}>
        <div className={styles.centered}>
          <Text className={styles.earnLabel} size="small">
            {i18n("FareOptionDetails__EARN_LABEL")}
          </Text>
          <Currency {...getPointsAccrualProps()} />
        </div>
        <div className={styles.centered}>
          <Currency {...getFareAmountProps()} />
        </div>
        <div className={styles.centered}>
          <BulletList {...getBulletListProps()} />
        </div>
        <div className={classNames(styles.centered, styles.continueButtonLayout)}>
          <Button styleType="primary">{i18n("FareOptionDetails__CONTINUE_BUTTON_LABEL")}</Button>
        </div>
      </Card>
    </div>
  );

  function getCardProps() {
    return {
      headingProps: {
        children: name,
        className: styles.centered,
        styleLevel: 4,
      },
    };
  }

  function getPointsAccrualProps() {
    return {
      amount: accrualPoints,
      currencyCode: "POINTS",
      superScriptSymbol: true,
      textProps: {
        size: "small",
      },
    };
  }

  function getFareAmountProps() {
    return {
      amount,
      currencyCode,
      roundingType: parseFloat(amount) > 0 ? "up" : "down",
      superScriptSymbol: true,
      textProps: {
        bold: true,
        className: styles.currencyCentered,
        size: "xsmall",
        textType: "display",
      },
    };
  }

  function getBulletListProps() {
    return {
      itemClassName: styles.itemClassName,
      items: features.map((feature) => ({
        content: feature.description,
        icon: feature.included
          ? { color: "var(--fare-option-details-text)", name: "Check", size: "16" }
          : {
              color: "var(--fare-option-details-text-unavailable)",
              name: "NotPermitted",
              size: "16",
            },
      })),
      size: "small",
    };
  }
};

FareOptionDetails.propTypes = {
  fareOptionDetails: PropTypes.shape({
    /* The fare amount. */
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

    /* Points that will be accrued for this fare option. */
    accrualPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

    /* Currency code for the fare amount (e.g., 'USD') */
    currencyCode: PropTypes.string.isRequired,

    /* Name of the fare option. */
    name: PropTypes.string.isRequired,

    /* A list of features for the option of the fare option and a boolean value indicating if the feature is available. */
    features: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string.isRequired,
        included: PropTypes.bool.isRequired,
      })
    ).isRequired,
  }).isRequired,

  /** Meta data used for the intersection observer */
  metadata: PropTypes.object,
};
